import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper, TextField, Switch, FormControlLabel, Divider, Avatar } from '@mui/material';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getOrCreateUserProfile, updateUserProfile } from '../utils/userService';

function UserSettings() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const profile = await getOrCreateUserProfile(user);
        setUserProfile(profile);
        setDisplayName(profile.displayName || '');
        setEmailNotifications(profile.settings?.emailNotifications ?? true);
      } else {
        navigate('/');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      await updateUserProfile(user.uid, {
        displayName,
        settings: {
          ...userProfile.settings,
          emailNotifications
        }
      });
      alert('✅ Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0a0a0a' }}>
        <Typography sx={{ color: '#fafafa', fontFamily: 'monospace' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#171717',
        borderBottom: '2px solid #262626',
        py: 4,
        px: 3
      }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontFamily: 'monospace',
                  color: '#fafafa',
                  fontWeight: 300,
                  mb: 1
                }}
              >
                ⚙️ Settings
              </Typography>
              <Typography sx={{ color: '#737373', fontFamily: 'monospace' }}>
                Manage your account preferences
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/')}
              variant="outlined"
              sx={{
                color: '#fafafa',
                borderColor: '#262626',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#525252',
                  bgcolor: '#171717'
                }
              }}
            >
              ← Back to App
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, bgcolor: '#171717', border: '1px solid #262626', borderRadius: '12px' }}>
          {/* Profile Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
              Profile Information
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar 
                src={user?.photoURL} 
                alt={user?.displayName || user?.email}
                sx={{ 
                  width: 80, 
                  height: 80,
                  bgcolor: '#262626',
                  color: '#fafafa',
                  fontSize: '2rem',
                  fontFamily: 'monospace',
                  mr: 3
                }}
              >
                {(user?.displayName || user?.email || '?')[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '1.1rem' }}>
                  {user?.displayName || 'User'}
                </Typography>
                <Typography sx={{ color: '#737373', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {user?.email}
                </Typography>
                <Typography sx={{ color: '#737373', fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.5 }}>
                  Role: {userProfile?.role || 'user'}
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': {
                  color: '#737373',
                  fontFamily: 'monospace'
                },
                '& .MuiOutlinedInput-root': {
                  color: '#fafafa',
                  fontFamily: 'monospace',
                  '& fieldset': {
                    borderColor: '#262626'
                  },
                  '&:hover fieldset': {
                    borderColor: '#525252'
                  }
                }
              }}
            />

            <TextField
              label="Email"
              value={user?.email || ''}
              disabled
              fullWidth
              sx={{ 
                '& .MuiInputLabel-root': {
                  color: '#737373',
                  fontFamily: 'monospace'
                },
                '& .MuiOutlinedInput-root': {
                  color: '#737373',
                  fontFamily: 'monospace',
                  '& fieldset': {
                    borderColor: '#262626'
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ borderColor: '#262626', my: 4 }} />

          {/* Preferences Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
              Preferences
            </Typography>
            
            <FormControlLabel
              control={
                <Switch 
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#fafafa'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#525252'
                    }
                  }}
                />
              }
              label="Email Notifications"
              sx={{
                color: '#fafafa',
                fontFamily: 'monospace',
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'monospace'
                }
              }}
            />
          </Box>

          <Divider sx={{ borderColor: '#262626', my: 4 }} />

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#fafafa',
              color: '#0a0a0a',
              fontFamily: 'monospace',
              py: 1.5,
              '&:hover': {
                bgcolor: '#e5e5e5'
              },
              '&:disabled': {
                bgcolor: '#262626',
                color: '#737373'
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}

export default UserSettings;

